/* Chahal Agri Farms — Reviews / Per-unit (v24)
 * ---------------------------------------------------------
 * Standalone module so it can be added/updated without touching
 * chahal-custom.js. Depends on window.CAF_DATA (chahal-reviews-data.js),
 * which must load first (theme.liquid orders the <script defer> tags).
 *
 * v24: removed the PDP coupon box (section 3 in earlier versions). It
 * advertised a hardcoded discount code on every product page and its
 * "Apply Offer" link auto-applied that code via Shopify's /discount/CODE
 * route, which then stuck in the customer's checkout session even after
 * the code was deactivated. Contradicts the no-coupon-UI premium
 * positioning — removed rather than repaired.
 */
(function () {
  'use strict';

  /* ============================================================
     REVIEWS + PER-UNIT PRICING
     Data source: window.CAF_DATA (assets/chahal-reviews-data.js),
     baked from each product's custom.reviews_json metafield. No
     reviews app and no Storefront-API access, so all rendering is
     client-side from the baked blob. Everything below no-ops safely
     if CAF_DATA is missing.
     ============================================================ */
  var CAF = window.CAF_DATA;

  /* -- Shared helpers -- */
  function cafInr(n) { return '₹' + Math.round(n).toLocaleString('en-IN'); }

  function cafHandle() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function cafEsc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Fractional star row: grey base + gold overlay clipped to value%. */
  var CAF_STAR_PATH = 'M10 1.6l2.47 5.01 5.53.8-4 3.9.94 5.51L10 14.22 ' +
    '5.06 16.82 6 11.31l-4-3.9 5.53-.8z';
  function cafStarSvg() {
    return '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="' +
      CAF_STAR_PATH + '"/></svg>';
  }
  function cafStars(value, px) {
    var pct = Math.max(0, Math.min(100, (value / 5) * 100));
    var five = cafStarSvg() + cafStarSvg() + cafStarSvg() +
      cafStarSvg() + cafStarSvg();
    return '<span class="caf-stars" style="--caf-star-size:' + (px || 16) +
      'px" role="img" aria-label="' + value.toFixed(1) + ' out of 5 stars">' +
      '<span class="caf-stars__bg">' + five + '</span>' +
      '<span class="caf-stars__fill" style="width:' + pct + '%">' + five +
      '</span></span>';
  }

  /* -- 1. PRODUCT-CARD RATINGS (LIVE JUDGE.ME) --
     Inject a Judge.me preview badge under each card title so the card
     star count reads from the SAME live source as the PDP widget/badge
     (auto-updates as new reviews come in). Cards expose data-product-id
     (numeric); Judge.me hydrates the badge from that id. We no longer use
     the baked CAF_DATA counts here, which went stale after new reviews.
     After injecting, ask Judge.me to (re)scan so cards added by
     filters/infinite-scroll also hydrate. */
  function cafInjectCardRatings() {
    var cards = document.querySelectorAll('.product-card[data-product-id]');
    if (!cards.length) return;
    var injected = false;
    cards.forEach(function (card) {
      if (card.dataset.cafRated) return;
      var pid = card.getAttribute('data-product-id');
      if (!pid) return;
      card.dataset.cafRated = '1';

      var badge = document.createElement('div');
      badge.className = 'jdgm-widget jdgm-preview-badge caf-card-jdgm';
      badge.setAttribute('data-id', pid);
      badge.setAttribute('data-template', 'index');

      var titleBlock = card.querySelector('[class*="_product_title"]');
      if (titleBlock) {
        titleBlock.insertAdjacentElement('afterend', badge);
      } else {
        var content = card.querySelector('.product-card__content');
        if (content) { content.insertBefore(badge, content.firstChild); }
        else { return; }
      }
      injected = true;
    });

    if (injected) {
      try {
        if (typeof window.jdgmReinitializeBadges === 'function') {
          window.jdgmReinitializeBadges();
        } else if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
          window.jdgm.customizeBadges();
        }
      } catch (e) {}
    }
  }

  /* -- 2. PER-UNIT RATE UNDER EACH VARIANT PILL --
     Parses size from the variant title and divides the baked price to
     show ₹price + ₹/L or ₹/kg, like Anveshan's variant grid. */
  function cafUnitRate(sizeStr, price) {
    var m = String(sizeStr).match(/([\d.]+)\s*(ml|kg|g|gm|gram|grams|l|litre|liter|ltr|litres)/i);
    if (!m) return null;
    var qty = parseFloat(m[1]);
    var unit = m[2].toLowerCase();
    var base, label;
    if (unit === 'ml') { base = qty / 1000; label = 'L'; }
    else if (unit === 'g' || unit === 'gm' || unit === 'gram' || unit === 'grams') { base = qty / 1000; label = 'kg'; }
    else if (unit === 'kg') { base = qty; label = 'kg'; }
    else { base = qty; label = 'L'; } /* l / litre / liter / ltr */
    if (!base) return null;
    return cafInr(price / base) + '/' + label;
  }

  function cafInjectUnitRates() {
    if (!CAF || !CAF.variants) return;
    var handle = cafHandle();
    var vars = handle && CAF.variants[handle];
    if (!vars) return;
    var byId = {};
    vars.forEach(function (v) { byId[v.id] = v; });

    document.querySelectorAll('.variant-option__button-label').forEach(function (label) {
      if (label.dataset.cafUnit) return;
      var input = label.querySelector('input[type="radio"], input[type="hidden"]');
      if (!input) return;
      var v = byId[input.getAttribute('data-variant-id')];
      if (!v) {
        var val = input.getAttribute('value');
        v = vars.filter(function (x) { return x.title === val; })[0];
      }
      if (!v) return;
      var rate = cafUnitRate(v.title, v.price);
      if (!rate) return;
      label.dataset.cafUnit = '1';

      var meta = document.createElement('span');
      meta.className = 'caf-unit-rate';
      meta.innerHTML = '<span class="caf-unit-rate__price">' + cafInr(v.price) + '</span>' +
        '<span class="caf-unit-rate__per">' + rate + '</span>';
      var textEl = label.querySelector('.variant-option__button-label__text') || label;
      textEl.appendChild(meta);
    });
  }

  /* -- 3. JUDGE.ME REVIEW HEADING (PDP) --
     Reviews now live in the Judge.me app widget (#judgeme_product_reviews).
     We only prepend the brand heading "Lab-Tested. Family-Trusted." above
     the widget so the section matches Chahal's voice. The old baked review
     section + JSON-LD were removed — Judge.me renders its own widget and
     review schema, so keeping them would duplicate content. */
  function cafInjectReviewHeading() {
    var widget = document.getElementById('judgeme_product_reviews') ||
      document.querySelector('.jdgm-review-widget');
    if (!widget) return;
    if (document.getElementById('caf-jdgm-heading')) return;

    var head = document.createElement('div');
    head.id = 'caf-jdgm-heading';
    head.className = 'caf-jdgm-heading';
    head.innerHTML = '<h2 class="caf-jdgm-heading__title">Lab-Tested. Family-Trusted.</h2>' +
      '<p class="caf-jdgm-heading__sub">Real reviews from families who switched to Chahal.</p>';
    widget.parentNode.insertBefore(head, widget);
  }

  /* -- 4. PDP REVIEW MEDIA GALLERY — "TRUSTED BY REAL FAMILIES" --
     Anveshan-style horizontal strip of customer photos. HYBRID source:
     curated brand photos (CAF_DATA.media[handle]) render first, then any
     live Judge.me review photos already on the page are appended and
     de-duped. Hover/focus a tile to reveal rating + reviewer name.
     Clicking a LIVE Judge.me tile re-triggers Judge.me's own photo so its
     native lightbox opens; clicking a CURATED tile opens a built-in
     lightbox (curated photos have no Judge.me element to click).
     Placement: #caf-jdgm-heading → strip → widget. PDP only. Idempotent. */

  function cafStripRating(card) {
    var el = card.querySelector('.jdgm-rev__rating');
    if (el) {
      var s = el.getAttribute('data-score');
      if (s) { var n = parseFloat(s); if (!isNaN(n)) return n; }
    }
    var on = card.querySelectorAll('.jdgm-rev__rating .jdgm--on');
    if (!on.length) on = card.querySelectorAll('.jdgm--on');
    return on.length ? on.length : null;
  }

  function cafStripPhotoUrl(img) {
    if (!img) return null;
    var url = img.getAttribute('src') ||
      img.getAttribute('data-src') ||
      img.getAttribute('data-large') || '';
    if (!url || /^data:/.test(url)) {
      var bg = (img.style && img.style.backgroundImage) || '';
      var m = bg.match(/url\(["']?([^"')]+)["']?\)/);
      url = m ? m[1] : '';
    }
    if (!url || /^data:/.test(url)) return null;
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url;
  }

  /* Curated customer photos for this product, from the baked data. */
  function cafCuratedMedia() {
    var handle = cafHandle();
    if (!handle || !CAF || !CAF.media) return [];
    var list = CAF.media[handle];
    if (!list || !list.length) return [];
    return list.map(function (m) {
      return {
        url: m.url,
        author: m.author || '',
        location: m.location || '',
        rating: (typeof m.rating === 'number') ? m.rating : 5,
        curated: true
      };
    });
  }

  /* Built-in lightbox for curated photos (live JM tiles use JM's own). */
  function cafOpenPhotoLightbox(url, author, location) {
    var lb = document.getElementById('caf-strip-lightbox');
    if (!lb) {
      lb = document.createElement('div');
      lb.id = 'caf-strip-lightbox';
      lb.className = 'caf-strip-lightbox';
      lb.innerHTML = '<div class="caf-strip-lightbox__inner">' +
        '<button type="button" class="caf-strip-lightbox__close" aria-label="Close photo">&times;</button>' +
        '<img class="caf-strip-lightbox__img" alt="Customer review photo">' +
        '<div class="caf-strip-lightbox__cap"></div></div>';
      document.body.appendChild(lb);
      lb.addEventListener('click', function (e) {
        if (e.target === lb || (e.target.className &&
            String(e.target.className).indexOf('caf-strip-lightbox__close') !== -1)) {
          lb.classList.remove('is-open');
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) lb.classList.remove('is-open');
      });
    }
    lb.querySelector('.caf-strip-lightbox__img').src = url;
    var cap = author ? (author + (location ? ' · ' + location : '')) : '';
    lb.querySelector('.caf-strip-lightbox__cap').textContent = cap;
    lb.classList.add('is-open');
  }

  /* Toggle the --scroll modifier (shows arrows + switches to left-aligned
     scrolling) only when the tiles actually overflow the track width. */
  function cafStripCheckOverflow(strip) {
    if (!strip) return;
    var track = strip.querySelector('.caf-review-strip__track');
    if (!track) return;
    var over = (track.scrollWidth - track.clientWidth) > 4;
    strip.classList.toggle('caf-review-strip--scroll', over);
  }

  function cafInjectReviewPhotoStrip() {
    if (!cafHandle()) return;
    var widget = document.getElementById('judgeme_product_reviews') ||
      document.querySelector('.jdgm-review-widget');
    if (!widget) return;

    var photos = [];
    var seen = {};

    /* Curated brand photos first. */
    cafCuratedMedia().forEach(function (m) {
      if (!m.url || seen[m.url]) return;
      seen[m.url] = 1;
      photos.push(m);
    });

    /* Then live Judge.me review photos, de-duped against curated. */
    var cards = widget.querySelectorAll('.jdgm-rev');
    Array.prototype.forEach.call(cards, function (card) {
      var img = card.querySelector('.jdgm-rev__pic-img');
      if (!img) return;
      var url = cafStripPhotoUrl(img);
      if (!url || seen[url]) return;
      seen[url] = 1;
      var authorEl = card.querySelector('.jdgm-rev__author');
      var author = authorEl ? (authorEl.textContent || '').trim() : '';
      photos.push({
        url: url,
        author: author,
        location: '',
        rating: cafStripRating(card),
        img: img
      });
    });

    var sig = photos.map(function (p) { return p.url; }).join('|');
    var existing = document.getElementById('caf-review-strip');
    if (!photos.length) { return; }
    if (existing && existing.dataset.cafSig === sig) return;
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var strip = document.createElement('div');
    strip.id = 'caf-review-strip';
    strip.className = 'caf-review-strip';
    strip.dataset.cafSig = sig;

    var html = '<div class="caf-review-strip__head">' +
      '<h3 class="caf-review-strip__title">Trusted by Real Families</h3>' +
      '<span class="caf-review-strip__count">' + photos.length +
      ' photo' + (photos.length === 1 ? '' : 's') + '</span></div>' +
      '<div class="caf-review-strip__viewport">' +
      '<button type="button" class="caf-review-strip__arrow caf-review-strip__arrow--prev" aria-label="Previous photos">&#8249;</button>' +
      '<div class="caf-review-strip__track">';

    photos.forEach(function (p, i) {
      var stars = p.rating ? cafStars(p.rating, 13) : '';
      var label = (p.author || 'Verified Buyer') +
        (p.location ? ', ' + p.location : '');
      html += '<button type="button" class="caf-review-strip__tile" data-caf-idx="' + i +
        '" aria-label="Review photo by ' + cafEsc(p.author || 'customer') + '">' +
        '<img class="caf-review-strip__img" src="' + cafEsc(p.url) +
        '" alt="Customer review photo" loading="lazy">' +
        '<span class="caf-review-strip__overlay">' +
          '<span class="caf-review-strip__stars">' + stars + '</span>' +
          '<span class="caf-review-strip__name">' + cafEsc(label) + '</span>' +
        '</span></button>';
    });

    html += '</div>' +
      '<button type="button" class="caf-review-strip__arrow caf-review-strip__arrow--next" aria-label="Next photos">&#8250;</button>' +
      '</div>';
    strip.innerHTML = html;

    var track = strip.querySelector('.caf-review-strip__track');
    strip.querySelector('.caf-review-strip__arrow--prev').addEventListener('click', function () {
      track.scrollBy({ left: -Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
    });
    strip.querySelector('.caf-review-strip__arrow--next').addEventListener('click', function () {
      track.scrollBy({ left: Math.round(track.clientWidth * 0.8), behavior: 'smooth' });
    });

    Array.prototype.forEach.call(strip.querySelectorAll('.caf-review-strip__tile'), function (tile) {
      tile.addEventListener('click', function () {
        var idx = parseInt(tile.getAttribute('data-caf-idx'), 10);
        var p = photos[idx];
        if (!p) return;
        if (p.img) { p.img.click(); }
        else { cafOpenPhotoLightbox(p.url, p.author, p.location); }
      });
    });

    var heading = document.getElementById('caf-jdgm-heading');
    if (heading && heading.parentNode) {
      heading.insertAdjacentElement('afterend', strip);
    } else if (widget.parentNode) {
      widget.parentNode.insertBefore(strip, widget);
    }
    setTimeout(function () { cafStripCheckOverflow(strip); }, 0);
  }

  /* -- Run + re-run on dynamic re-renders (variant change morphs the
     picker; filters/infinite-scroll add cards) -- */
  function cafRunAll() {
    try { cafInjectCardRatings(); } catch (e) {}
    try { cafInjectUnitRates(); } catch (e) {}
    try { cafInjectReviewHeading(); } catch (e) {}
    try { cafInjectReviewPhotoStrip(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cafRunAll);
  } else {
    cafRunAll();
  }
  setTimeout(cafRunAll, 600);
  setTimeout(cafRunAll, 1500);

  var cafReviewDebounce;
  var cafReviewObserver = new MutationObserver(function () {
    clearTimeout(cafReviewDebounce);
    cafReviewDebounce = setTimeout(function () {
      try { cafInjectCardRatings(); } catch (e) {}
      try { cafInjectUnitRates(); } catch (e) {}
      try { cafInjectReviewHeading(); } catch (e) {}
      try { cafInjectReviewPhotoStrip(); } catch (e) {}
    }, 200);
  });
  /* Scoped to #MainContent (not document.body) so unrelated DOM churn in the
     header/footer/cart-drawer/WhatsApp widget doesn't re-trigger these scans
     on every interaction — flagged as a mobile INP contributor. */
  var cafObserveTarget = document.getElementById('MainContent') || document.body;
  if (cafObserveTarget) {
    cafReviewObserver.observe(cafObserveTarget, { childList: true, subtree: true });
  }

  /* Re-evaluate strip overflow on resize so arrows appear/disappear as the
     available width changes (registered once). */
  var cafResizeDebounce;
  window.addEventListener('resize', function () {
    clearTimeout(cafResizeDebounce);
    cafResizeDebounce = setTimeout(function () {
      cafStripCheckOverflow(document.getElementById('caf-review-strip'));
    }, 150);
  });

})();
